// The training-load model behind "Fitness & Freshness" (Banister's
// impulse-response model, in the form TrainingPeaks / intervals.icu use):
//
//   fitness_t = fitness_t-1 + (load_t − fitness_t-1) / 42   (CTL, slow)
//   fatigue_t = fatigue_t-1 + (load_t − fatigue_t-1) / 7    (ATL, fast)
//   form_t    = fitness_t − fatigue_t                         (TSB)
//
// fitness.json ships the three curves but not the daily loads, so we invert
// the fatigue update to get them back: load_t = atl_t-1 + 7·(atl_t − atl_t-1).
import { ACCENT, BLUE, GOLD, MUTED, RED } from "../theme";

export const CTL_TAU = 42;
export const ATL_TAU = 7;
export const halfLife = (tau) => tau * Math.LN2;

export const step = (prev, load) => {
  const ctl = prev.ctl + (load - prev.ctl) / CTL_TAU;
  const atl = prev.atl + (load - prev.atl) / ATL_TAU;
  return { ctl, atl, tsb: ctl - atl };
};

// fitness series [{d, ctl, atl, tsb}] → same rows plus the recovered `load`
export const withLoads = (series) =>
  series.map((f, i) => {
    if (!i) return { ...f, load: null };
    const prev = series[i - 1];
    const fromAtl = prev.atl + ATL_TAU * (f.atl - prev.atl);
    return { ...f, load: Math.max(0, Math.round(fromAtl)) };
  });

// run the model forward from a starting state over a list of daily loads
export const simulate = (start, loads) => {
  const out = [];
  let s = { ctl: start.ctl, atl: start.atl };
  loads.forEach((l) => {
    s = step(s, l);
    out.push({ ...s, load: l });
  });
  return out;
};

// what one session of `load` on day 0 contributes to each curve, day by day
export const impulse = (load, days) =>
  [...Array(days)].map((_, t) => {
    const ctl = (load / CTL_TAU) * (1 - 1 / CTL_TAU) ** t;
    const atl = (load / ATL_TAU) * (1 - 1 / ATL_TAU) ** t;
    return { t, ctl, atl, tsb: ctl - atl };
  });

// day on which a single session stops costing form and starts paying it back
export const breakEvenDay = () => {
  const r = Math.log(CTL_TAU / ATL_TAU) / Math.log((1 - 1 / CTL_TAU) / (1 - 1 / ATL_TAU));
  return Math.round(r);
};

export const FORM_ZONES = [
  {
    key: "over",
    name: "Overreaching",
    min: -Infinity,
    max: -30,
    color: RED,
    blurb: "Digging a hole. Fine for a few days, risky for longer — injury and illness live here.",
  },
  {
    key: "build",
    name: "Building",
    min: -30,
    max: -10,
    color: ACCENT,
    blurb: "The productive zone. Tired legs, rising fitness — this is where training actually happens.",
  },
  {
    key: "hold",
    name: "Maintaining",
    min: -10,
    max: 5,
    color: MUTED,
    blurb: "The grey zone. Not tired, not getting much fitter either.",
  },
  {
    key: "fresh",
    name: "Fresh",
    min: 5,
    max: 25,
    color: BLUE,
    blurb: "Rested and sharp. Peak for a match or a race here.",
  },
  {
    key: "detrain",
    name: "Detraining",
    min: 25,
    max: Infinity,
    color: GOLD,
    blurb: "Very fresh — and losing fitness. Great for a holiday, not for a season.",
  },
];

export const zoneFor = (tsb) => FORM_ZONES.find((z) => tsb >= z.min && tsb < z.max) || FORM_ZONES[2];

const quantile = (arr, p) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  return s[Math.min(s.length - 1, Math.max(0, Math.round(p * (s.length - 1))))];
};
const round5 = (v) => Math.max(5, Math.round(v / 5) * 5);

// session sizes for the planner, calibrated to my own history. Named by size,
// not effort — activity names already use Easy/Steady/Solid/Spicy for effort.
export const loadLevels = (rows) => {
  const trained = rows.filter((r) => r.load > 5).map((r) => r.load);
  return [
    { key: "rest", label: "Rest", load: 0 },
    { key: "light", label: "Light", load: round5(quantile(trained, 0.25) || 40) },
    { key: "medium", label: "Medium", load: round5(quantile(trained, 0.6) || 90) },
    { key: "big", label: "Big", load: round5(quantile(trained, 0.9) || 180) },
  ];
};

// preset weekly patterns for the planner (indices into loadLevels; Monday first)
export const PRESETS = [
  { key: "rest", label: "Full rest", week: [0, 0, 0, 0, 0, 0, 0] },
  { key: "usual", label: "My usual week", week: null },
  { key: "build", label: "Build block", week: [2, 3, 1, 3, 0, 3, 2] },
  { key: "taper", label: "Taper for a match", taper: true },
];

// my typical week, in levels: for each weekday, the median level I've trained at
export const usualWeek = (rows, levels) => {
  const byDow = [...Array(7)].map(() => []);
  rows.slice(-56).forEach((r) => {
    if (r.load == null) return;
    const dow = (new Date(`${r.d}T12:00:00`).getDay() + 6) % 7;
    byDow[dow].push(r.load);
  });
  return byDow.map((loads) => {
    const m = quantile(loads, 0.5);
    let best = 0;
    levels.forEach((l, i) => {
      if (Math.abs(l.load - m) < Math.abs(levels[best].load - m)) best = i;
    });
    return best;
  });
};

// the final week of a taper: search every week whose load only steps down,
// with at most two rest days and ≤60% of a usual week's volume, for the one
// that ends Fresh with the most fitness
const TAPER_TARGET = 15;
const TAPER_VOLUME = 0.6;
const bestTaper = (start, levels, cap) => {
  const n = levels.length;
  let best = null;
  for (let k = 0; k < n ** 7; k++) {
    const t = [];
    let x = k;
    for (let j = 0; j < 7; j++) {
      t.push(x % n);
      x = Math.floor(x / n);
    }
    if (t.some((v, j) => j && v > t[j - 1]) || t.filter((v) => !v).length > 2) continue;
    if (t.reduce((sum, i) => sum + levels[i].load, 0) > cap) continue;
    const end = simulate(start, t.map((i) => levels[i].load)).pop();
    const miss = Math.abs(end.tsb - TAPER_TARGET);
    const fresh = end.tsb >= 5 && end.tsb < 25;
    const score = fresh ? end.ctl - 0.05 * miss : -1000 - miss;
    if (!best || score > best.score) best = { score, t };
  }
  return best ? best.t : [1, 1, 1, 1, 1, 0, 0];
};

// ctx: { days, startDow, levels, usual, start } — start is today's {ctl, atl}
export const presetPlan = (preset, { days, startDow, levels, usual, start }) => {
  const weekly = (week) => [...Array(days)].map((_, i) => week[(startDow + i) % 7]);
  if (!preset.taper) return weekly(preset.week || usual);
  const head = weekly(usual).slice(0, Math.max(0, days - 7));
  const mid = head.length ? simulate(start, head.map((i) => levels[i].load)).pop() : start;
  const cap = TAPER_VOLUME * usual.reduce((sum, i) => sum + levels[i].load, 0);
  return [...head, ...bestTaper(mid, levels, cap)];
};
